import { useContext, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import TaskManagementConfigDto from "../../../types/dtos/forms/TaskManagementConfigDto";
import {
  FormType,
  ValidationResult,
  ValidationSettings,
} from "../../../types/forms";
import ValidationWarning from "../../common/ValidationWarning";
import UserContext from "../../../state/UserContext";
import AdvancedTaskDto from "../../../types/dtos/forms/AdvancedTaskDto";
import { t } from "i18next";
import ExistingAdvancedTaskPopup from "../../tasks/advanced-tasks/ExistingAdvancedTaskPopup";
import advancedTasksApi from "../../../api/task/advancedTasksApi";
import { useAuth } from "react-oidc-context";
import UserTaskListingForJourney from "../../../types/dtos/tasks/advanced-tasks/UserTaskListingForJourney";
import AddAdvancedTaskPopup from "../../tasks/advanced-tasks/AddAdvancedTaskPopup";
import {
  BaseUserDetailsDto,
  UserBasicDetailsDto,
} from "../../../types/dtos/generic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import taskTypeIconHelper from "../../../helpers/taskTypeIconHelper";
import TaskRequirementBadge from "../../task-management/TaskRequirementBadge";
import { TaskManagementDropDownMenuArgs } from "../../../types/tasks/TaskManagementDropDownMenuArgs";
import { ExistingTaskManagementTabType } from "../../../types/tasks/ExistingTaskManagementTabType";
import { TaskPopupViewType } from "../../../types/tasks/TaskPopupViewType";
import AdvancedTaskDesktopItem from "./task-management/AdvancedTaskDesktopItem";
import AdvancedTaskMobileItem from "./task-management/AdvancedTaskMobileItem";
import { advancedTaskHelper, userDetailsHelper } from "../../../helpers";
import EnforcedCommentsValidityBanner from "./task-management/EnforcedCommentsValidityBanner";
import TaskWeightingBanner from "./task-management/TaskWeightingBanner";
import { TaskCreationMinMaxCountPrompt } from "./TaskCreationMinMaxCountPrompt";
import WarningAlert from "../../alerts/WarningAlert";
import AdvancedTaskTypeSwapabilityDto from "../../../types/dtos/forms/AdvancedTaskTypeSwapabilityDto";
import ResultStatus from "../../../types/dtos/generic/ResultStatus";
import { shouldShowTaskWeightingBanner } from "../../../helpers/taskWeightingHelper";
import {
  allowsTaskCreation,
  isReviewQuestionMode,
  isViewOnlyMode,
} from "../../../helpers/advancedTaskQuestionModeHelper";
import { isExistingAdvancedTaskPopupLocked } from "../../../helpers/existingAdvancedTaskPopupLockHelper";
import {
  isDualPrepPlanningCheckIn,
  isDualPrepPlanningForm,
} from "../../../helpers/dualPrepTaskQuestionHelper";

interface TaskManagementQuestionProps {
  taskTypeId: string;
  tasks: AdvancedTaskDto[];
  /** The validation settings from the FormQuestion */
  questionValidations: ValidationSettings;
  cancelledTasks: AdvancedTaskDto[];
  newTasks: AdvancedTaskDto[];
  liveAndNewTasks: AdvancedTaskDto[];
  formType: FormType;
  formId: number;
  subjectUser: BaseUserDetailsDto;
  config: TaskManagementConfigDto;
  isReadOnly: boolean;
  /** Whether or not to display the validation warnings */
  showValidationErrors: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  answerSetUniqueId: string | null | undefined;
  answerSetDateCreated: Date | null | undefined;
  participants: Array<UserBasicDetailsDto>;
  advancedTaskTypeSwapabilities: AdvancedTaskTypeSwapabilityDto[] | null;
  questionId?: string;
  /** Whether a dual-prep collab doc is in its prep (planning) phase. Journeys are always planning */
  isInPrepMode?: boolean;

  onPreTaskInteractionCheckForAnswerSet?(
    onSaveSuccess: (
      answerSetUniqueId: string | null,
      answerSetDateCreated: Date | null
    ) => void,
    onSaveError: () => void
  ): void;
  onChangeAdvancedTasks(
    tasks?: AdvancedTaskDto[],
    cancelledTasks?: AdvancedTaskDto[],
    newTasks?: AdvancedTaskDto[]
  ): void;
  onSwapTask?(
    taskId: string,
    fromQuestionId: string,
    toQuestionId: string,
    isSwappingToNew: boolean
  ): void;
}

function TaskManagementQuestion({
  taskTypeId,
  tasks,
  questionValidations,
  cancelledTasks,
  newTasks,
  liveAndNewTasks,
  formType,
  formId,
  subjectUser,
  config,
  isReadOnly,
  showValidationErrors,
  validationResult = null,
  answerSetUniqueId,
  answerSetDateCreated,
  participants,
  advancedTaskTypeSwapabilities,
  questionId,
  isInPrepMode = undefined,
  onPreTaskInteractionCheckForAnswerSet = undefined,
  onChangeAdvancedTasks,
  onSwapTask,
}: TaskManagementQuestionProps) {
  // Context
  const userContext = useContext(UserContext);
  const auth = useAuth();
  const tasksApi = new advancedTasksApi(auth.user?.access_token);

  // View-only locks the list and popup without requiring JourneyQuestion to pass isReadOnly.
  const effectiveReadOnly = isReadOnly || isViewOnlyMode(config.questionMode);

  // State
  const [currentAnswerSetUniqueId, setCurrentAnswerSetUniqueId] = useState<
    string | null | undefined
  >(answerSetUniqueId);
  const [currentAnswerSetDateCreated, setCurrentAnswerSetDateCreated] =
    useState<Date | null | undefined>(answerSetDateCreated);

  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState<boolean>(false);
  const [isExistingTaskModalOpen, setIsExistingTaskModalOpen] =
    useState<boolean>(false);
  const [selectedUserTaskId, setSelectedUserTaskId] = useState<string | null>(
    null
  );
  const [selectedTaskSnapshotDate, setSelectedTaskSnapshotDate] =
    useState<Date | null>(null);
  const [selectedUserTaskIsNewlyCreated, setSelectedUserTaskIsNewlyCreated] =
    useState<boolean>(false);
  const [selectedUserTaskTabToOpenTo, setSelectedUserTaskTabToOpenTo] =
    useState<ExistingTaskManagementTabType | null>(null);
  const [selectedFormToDisplay, setSelectedFormToDisplay] =
    useState<TaskPopupViewType | null>(null);

  // Only really to be set if the enforced commments are enabled and we are in a review question mode
  const [showAddCommentInFormLikeStyle, setShowAddCommentInFormLikeStyle] =
    useState<boolean>(false);

  const isLoggedInUserTheSubjectUser =
    subjectUser.userId === userContext.user.id;
  const nonSubjectUser = participants.find(
    (p) => p.userId !== subjectUser.userId
  );
  const subjectUserFullDetails = participants.find(
    (p) => p.userId === subjectUser.userId
  );

  const nonSubjectUserName = userDetailsHelper.getDisplayName(
    nonSubjectUser?.userId!,
    participants
  );
  const subjectUserName = userDetailsHelper.getDisplayName(
    subjectUser?.userId!,
    participants
  );

  // Get the swapability info for this task type to determine if we're in review or setting question
  const swapabilityInfo = advancedTaskTypeSwapabilities?.find(
    (x) => x.taskTypeId === taskTypeId
  );
  const isReviewQuestion = swapabilityInfo?.reviewQuestionId === questionId;
  const isSettingQuestion = swapabilityInfo?.settingQuestionId === questionId;

  // Calculate activeTasks using the same dataset that's displayed in the UI based on questionMode
  // This matches the logic in EnforcedCommentsValidityBanner to ensure consistency
  const taskDataSet = useMemo(
    () =>
      advancedTaskHelper.getTaskManagementQuestionTaskSet({
        questionMode: config.questionMode,
        tasks,
        newTasks,
        liveAndNewTasks,
      }),
    [config.questionMode, tasks, newTasks, liveAndNewTasks]
  );

  const activeTasks = useMemo(
    () => taskDataSet.filter((t) => t.status === "ACTIVE"),
    [taskDataSet]
  );

  const overdueActiveTasks = useMemo(
    () => taskDataSet.filter((t) => t.status === "ACTIVE" && t.isOverdue),
    [taskDataSet]
  );

  // On the planning half of a dual-prep pairing the chasing banners are deferred to the meeting
  // document, but the per-task Overdue badge and comment prompts still show
  const isDualPrepPlanning = isDualPrepPlanningCheckIn(
    config,
    formType,
    isInPrepMode
  );
  const isDualPrepPlanningFormContext = isDualPrepPlanningForm(
    config,
    formType,
    isInPrepMode
  );

  const showOverdueTasksBanner =
    !effectiveReadOnly && !isDualPrepPlanning && overdueActiveTasks.length > 0;

  useEffect(() => {
    setCurrentAnswerSetUniqueId(answerSetUniqueId);
  }, [answerSetUniqueId]);

  useEffect(() => {
    setCurrentAnswerSetDateCreated(answerSetDateCreated);
  }, [answerSetDateCreated]);

  // Other
  const taskType = userContext.user.client.taskTypes.find(
    (t) => t.id === config.advancedTaskTypeId
  );

  const doesModeAllowForTaskCreation = allowsTaskCreation(config.questionMode);
  const showCreateTaskButton = !effectiveReadOnly && doesModeAllowForTaskCreation;

  const doesTaskTypeHaveRestrictions = config.countRestriction !== "NONE";
  const doesModeEnforceRestrictions = allowsTaskCreation(config.questionMode);

  const userHasTasksToShow = advancedTaskHelper.questionHasTasksToShow({
    questionMode: config.questionMode,
    tasks,
    cancelledTasks,
    newTasks,
  });

  const cancelledTasksToShow = advancedTaskHelper.getCancelledTasksForQuestion({
    questionMode: config.questionMode,
    cancelledTasks,
  });

  let emptyStateMessageTranslationKey =
    subjectUser.userId == userContext.user.id
      ? "Pages.MyDashboard.TaskEmptyState"
      : "Pages.MyDashboard.TaskEmptyState_Manager";
  if (config.questionMode === "TASK-CREATION-ONLY") {
    emptyStateMessageTranslationKey += "_CreationOnly";
  }

  const emptyStateMessage = t(emptyStateMessageTranslationKey, {
    person: subjectUser.firstName,
    taskType: advancedTaskHelper.ToLowerCase(
      taskType?.pluralNameTranslationKeyIdentifier || "tasks"
    ),
  });

  // If config allows rating column to be shown AND we are not in the journey OR we
  // are in the journey and the tasktype has a ReviewForm. Dual-prep planning hides
  // the column so people cannot rate before the meeting.
  const displayRatingColumn = advancedTaskHelper.shouldDisplayTaskRatingColumn({
    showRatingColumn: config.showRatingColumn,
    formType,
    hasReviewForm: !!taskType?.reviewFormId,
    isDualPrepPlanningForm: isDualPrepPlanningFormContext,
  });
  const columnCount = displayRatingColumn ? 4 : 3;

  const onRefreshTasks = () => {
    tasksApi.getTasksForJourney(
      config.subjectEmployeeId,
      config.advancedTaskTypeId,
      config.minAdvancedTaskReviewDate,
      config.advancedTaskReviewDateTypeEnum,
      config.enforceComments,
      config.excludeTasksCompletedBeforeJourney,
      config.questionMode,
      config.answerSetId,
      currentAnswerSetUniqueId,
      currentAnswerSetDateCreated,
      config.isCompleted,
      config.isDualPrepJourney,
      (data: UserTaskListingForJourney) => {
        onChangeAdvancedTasks(data.tasks, data.cancelledTasks, data.newTasks);
      },
      (error: any) => {
        console.error("Error getting tasks on tab change", error);
      }
    );
  };

  const onAddNewTaskClick = () => {
    // Need an answer set to be able to store 'CreatedInAnswerSetUniqueId' against a task
    if (
      currentAnswerSetUniqueId == null ||
      currentAnswerSetUniqueId == undefined
    ) {
      requestAnswerSetCreation();
    }

    setIsAddTaskModalOpen(true);
  };

  const onAddNewTaskSuccessful = () => {
    setIsAddTaskModalOpen(false);
    onRefreshTasks();
  };

  const handleExistingModalOpenChange = (isOpen: boolean) => {
    setIsExistingTaskModalOpen(isOpen);
    if (!isOpen) {
      onRefreshTasks();
      setSelectedUserTaskTabToOpenTo(null);
      setSelectedFormToDisplay(null);
      setShowAddCommentInFormLikeStyle(false);
    }
  };

  const handleTaskDropDownItemClick = (userTaskId: string, action: string) => {
    // Handle swap actions first - these don't need the modal to open
    if (action === TaskManagementDropDownMenuArgs.SwapTask_ToNew) {
      if (!swapabilityInfo || !questionId || !onSwapTask) {
        console.error("Cannot swap task: missing required information");
        return;
      }

      const taskToSwap = liveAndNewTasks.find((t) => t.taskId === userTaskId);
      if (!taskToSwap) {
        console.error("Cannot swap task: task not found");
        return;
      }

      // Ensure we have an answer set before swapping
      if (
        currentAnswerSetUniqueId == null ||
        currentAnswerSetUniqueId == undefined
      ) {
        requestAnswerSetCreation();
        // Wait for answer set to be created - we'll need to handle this differently
        // For now, just proceed and let the backend handle it
      }

      // Pass the task info to the parent component to make the API call with the destination question's config
      onSwapTask(
        userTaskId,
        questionId,
        swapabilityInfo.settingQuestionId,
        true // isSwappingToNew
      );
      return;
    }
    if (action === TaskManagementDropDownMenuArgs.SwapTask_ToReview) {
      if (!swapabilityInfo || !questionId || !onSwapTask) {
        console.error("Cannot swap task: missing required information");
        return;
      }

      const taskToSwap = liveAndNewTasks.find((t) => t.taskId === userTaskId);
      if (!taskToSwap) {
        console.error("Cannot swap task: task not found");
        return;
      }

      // Pass the task info to the parent component to make the API call with the destination question's config
      onSwapTask(
        userTaskId,
        questionId,
        swapabilityInfo.reviewQuestionId,
        false // isSwappingToNew
      );
      return;
    }

    // For all other actions, open the modal and proceed with existing logic
    handleExistingModalOpenChange(true);
    setSelectedUserTaskId(userTaskId);
    clearHighlight(userTaskId);

    const matchingTask = liveAndNewTasks.find((x) => x.taskId == userTaskId);
    if (matchingTask) {
      setSelectedUserTaskIsNewlyCreated(
        matchingTask.isNewlyCreatedInThisJourney
      );
    }

    // Any comments added need an answer set to be in place as we use the DateCreated on that
    // to include relevant comments for enforced comments logic.
    if (
      currentAnswerSetUniqueId == null ||
      currentAnswerSetUniqueId == undefined
    ) {
      requestAnswerSetCreation();
    }

    switch (action) {
      case TaskManagementDropDownMenuArgs.ViewDetails:
        setSelectedUserTaskTabToOpenTo("DETAILS");
        break;
      case TaskManagementDropDownMenuArgs.AddComment:
        setSelectedUserTaskTabToOpenTo("COMMENTS");

        // In this scenario we need to show the 'Add comment' form in the same style as the form
        if (
          formType === "COLLAB-DOC" &&
          config.enforceComments == true &&
          isReviewQuestionMode(config.questionMode)
        ) {
          setShowAddCommentInFormLikeStyle(true);
        }
        break;
      case TaskManagementDropDownMenuArgs.CompleteTask:
        setSelectedUserTaskTabToOpenTo("DETAILS");
        setSelectedFormToDisplay("CLOSE-TASK-FORM");
        break;
      case TaskManagementDropDownMenuArgs.CancelTask:
        setSelectedUserTaskTabToOpenTo("DETAILS");
        setSelectedFormToDisplay("CANCEL-TASK-FORM");
        break;
      case TaskManagementDropDownMenuArgs.ReopenTask:
        setSelectedUserTaskTabToOpenTo("DETAILS");
        setSelectedFormToDisplay("REOPEN-TASK");
        break;
      case TaskManagementDropDownMenuArgs.ReviewTask:
        setSelectedUserTaskTabToOpenTo("DETAILS");
        setSelectedFormToDisplay("REVIEW-TASK-FORM");
        break;
    }
  };

  const handleTaskViewButtonClick = (
    userTaskId: string,
    snapshotDate: Date
  ) => {
    handleExistingModalOpenChange(true);
    setSelectedUserTaskId(userTaskId);
    setSelectedTaskSnapshotDate(snapshotDate);
    clearHighlight(userTaskId);

    const matchingTask = liveAndNewTasks.find((x) => x.taskId == userTaskId);
    if (matchingTask) {
      setSelectedUserTaskIsNewlyCreated(
        matchingTask.isNewlyCreatedInThisJourney
      );
    }

    // Any comments added need an answer set to be in place as we use the DateCreated on that
    // to include relevant comments for enforced comments logic.
    if (
      currentAnswerSetUniqueId == null ||
      currentAnswerSetUniqueId == undefined
    ) {
      requestAnswerSetCreation();
    }

    setSelectedUserTaskTabToOpenTo("DETAILS");
  };

  /** If the task is highlighted for the current user, clear the highlight and update the state to remove the HighlightDot */
  const clearHighlight = (userTaskId: string) => {
    let isCancelledTask =
      cancelledTasks.find((x) => x.taskId == userTaskId) != null ? true : false;
    clearTaskHighlight(userTaskId, isCancelledTask);
  };

  const clearTaskHighlight = (userTaskId: string, isCancelledTask: boolean) => {
    const matchingTask = !isCancelledTask
      ? liveAndNewTasks.find((x) => x.taskId == userTaskId)
      : cancelledTasks.find((x) => x.taskId == userTaskId);

    if (!matchingTask) return;

    const newTasksArray = !isCancelledTask
      ? [...liveAndNewTasks]
      : [...cancelledTasks];
    const taskIndexToUpdate = newTasksArray.findIndex(
      (x) => x.taskId == userTaskId
    );
    if (
      matchingTask.ownerEmployeeId == userContext.user.id &&
      matchingTask.highlightToOwner
    ) {
      newTasksArray[taskIndexToUpdate].highlightToOwner = false;
      if (!isCancelledTask) {
        onChangeAdvancedTasks(newTasksArray, undefined);
      } else {
        onChangeAdvancedTasks(undefined, newTasksArray);
      }
      userContext.removeTaskHighlight(
        matchingTask.ownerEmployeeId,
        config.advancedTaskTypeId,
        userTaskId
      );
    } else if (
      matchingTask.ownerEmployeeId != userContext.user.id &&
      matchingTask.highlightToManager
    ) {
      newTasksArray[taskIndexToUpdate].highlightToManager = false;
      if (!isCancelledTask) {
        onChangeAdvancedTasks(newTasksArray, undefined);
      } else {
        onChangeAdvancedTasks(undefined, newTasksArray);
      }
      userContext.removeTaskHighlight(
        matchingTask.ownerEmployeeId,
        config.advancedTaskTypeId,
        userTaskId
      );
    }
  };

  const handleExistingModalRemoveTaskSuccess = () => {
    setIsExistingTaskModalOpen(false);
    onRefreshTasks();
  };

  // In certain scenarios we need an answer set to be created. This will request one from the API.
  const requestAnswerSetCreation = () => {
    if (onPreTaskInteractionCheckForAnswerSet) {
      onPreTaskInteractionCheckForAnswerSet(
        (answerSetGuidId: string | null, answerSetDateCreated: Date | null) => {
          setCurrentAnswerSetUniqueId(answerSetGuidId);
          setCurrentAnswerSetDateCreated(answerSetDateCreated);
        },
        () => {}
      );
    }
  };

  // The empty state button styling is different to when the button appears under the task list
  const journeyAddTaskButtonClassNames = userHasTasksToShow
    ? "!text-center py-1 bg-white/90 primary-colour rounded-md"
    : "journey-btn-primary";

  const addButtonComponent = (
    <>
      {taskType && (
        <>
          <button
            onClick={onAddNewTaskClick}
            disabled={
              taskType.maxTaskCount != null &&
              activeTasks &&
              activeTasks.length >= taskType.maxTaskCount
            }
            className={cx(
              "!px-3",
              formType === "JOURNEY"
                ? journeyAddTaskButtonClassNames
                : "btn-primary"
            )}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" size="sm" />
            {t("Common.Add")}{" "}
            {t(taskType.singularNameTranslationKeyIdentifier || "Task")}
          </button>
          <TaskCreationMinMaxCountPrompt
            validationSettings={questionValidations}
            taskType={taskType}
          />
        </>
      )}
    </>
  );

  // Get the validation counts from either the TaskType or from the FormQuestion validation settings
  const taskValidationCounts = advancedTaskHelper.getValidationCounts(
    taskType?.minTaskCount || null,
    taskType?.maxTaskCount || null,
    questionValidations
  );

  const taskTypeIcon = taskType
    ? taskTypeIconHelper.getTaskTypeIcon(taskType!.iconType, "LIGHT")
    : null;

  const showExtraColumnsBecauseOfEnforcedComments =
    taskType !== undefined &&
    taskType !== null &&
    config.enforceComments === true &&
    tasks.every((t) => t.enforcedComments != null) &&
    (config.questionMode == "TASK-CHECK-IN-AND-CREATION" ||
      config.questionMode == "TASK-CHECK-IN-ONLY");

  // Enrich validation errors with task type translation keys for FAILED-TASK-RESTRICTIONS errors
  const enrichedErrors = useMemo(() => {
    if (!validationResult?.errors || !taskType) {
      return validationResult?.errors;
    }
    return validationResult.errors.map((error) => {
      if (
        error.errorType === "FAILED-TASK-RESTRICTIONS" &&
        error.taskRestriction &&
        !error.taskRestriction.taskTypeTranslationKeyIdentifier
      ) {
        return {
          ...error,
          taskRestriction: {
            ...error.taskRestriction,
            taskTypeTranslationKeyIdentifier: taskType.pluralNameTranslationKeyIdentifier,
            taskTypeSingularTranslationKeyIdentifier: taskType.singularNameTranslationKeyIdentifier,
          },
        };
      }
      return error;
    });
  }, [validationResult?.errors, taskType]);

  const errorsForValidationWarning = useMemo(() => {
    const errors = enrichedErrors ?? validationResult?.errors ?? null;
    if (!errors) {
      return null;
    }
    if (!showOverdueTasksBanner) {
      return errors;
    }
    return errors.filter((error) => error.errorType !== "OVERDUE-TASKS");
  }, [enrichedErrors, validationResult?.errors, showOverdueTasksBanner]);

  return (
    <>
      {/* Validation errors */}
      {showValidationErrors &&
        validationResult &&
        errorsForValidationWarning &&
        errorsForValidationWarning.length > 0 && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={errorsForValidationWarning}
        />
      )}
      {userHasTasksToShow && (
        <>
          {showCreateTaskButton &&
            doesModeEnforceRestrictions &&
            doesTaskTypeHaveRestrictions &&
            taskType && (
              <div
                className={cx(
                  "text-right",
                  formType === "COLLAB-DOC" ? "pb-2 pt-1" : "pb-1 mb-2"
                )}
              >
                <TaskRequirementBadge
                  tasks={activeTasks}
                  minTasks={taskValidationCounts.min}
                  maxTasks={taskValidationCounts.max}
                  countRestriction={taskValidationCounts.restrictionType}
                  activeCategories={taskType.categories}
                  taskType={taskType}
                  isNonEmployeeViewer={false}
                  origin="JOURNEY"
                  journeySubjectFirstName={
                    // Only show the subject's name if the subject is not the current user
                    config.subjectEmployeeId == userContext.user.id
                      ? null
                      : subjectUser.firstName
                  }
                />
              </div>
            )}

          {/* Desktop Only */}
          <div className="hidden sm:contents">
            <div
              className={cx("mb-2", formType === "COLLAB-DOC" ? "mt-2" : "")}
            >
              {/* Table Header */}
              <div className="text-xs text-gray-700 uppercase bg-gray-100 border-b rounded-t-lg">
                <div className="flex">
                  <div className="flex-1 pl-12 pr-2 py-3">
                    {t("Tasks.Common.TaskName")}
                  </div>
                  <div className="w-32 text-center px-6 py-3">
                    {t("Pages.Admin.Common.Status")}
                  </div>

                  {showExtraColumnsBecauseOfEnforcedComments && !effectiveReadOnly && (
                    <div className="w-32 text-center px-6 py-3">Commented</div>
                  )}

                  {displayRatingColumn && (
                    <div className="w-24 text-center px-6 py-3">
                      {t("Common.Rating")}
                    </div>
                  )}

                  {false &&
                    showExtraColumnsBecauseOfEnforcedComments &&
                    !effectiveReadOnly &&
                    formType === "COLLAB-DOC" && (
                      <div className="w-32 text-center px-6 py-3">Reviewed</div>
                    )}

                  <div className="w-40 px-6 py-3"></div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y">
                {config.questionMode != "TASK-CREATION-ONLY" &&
                  tasks.map((task, index) => (
                    <AdvancedTaskDesktopItem
                      key={task.taskId}
                      userContext={userContext}
                      task={task}
                      taskType={taskType}
                      config={config}
                      isReadOnly={effectiveReadOnly}
                      formType={formType}
                      displayRatingColumn={displayRatingColumn}
                      columnCount={columnCount}
                      loggedInUserId={userContext.user.id}
                      isLoggedInUserTheSubjectUser={
                        isLoggedInUserTheSubjectUser
                      }
                      subjectUser={subjectUserFullDetails}
                      nonSubjectUser={nonSubjectUser}
                      participants={participants}
                      onDropdownMenuItemClick={handleTaskDropDownItemClick}
                      onViewButtonClick={handleTaskViewButtonClick}
                      taskTypeIcon={taskTypeIcon}
                      isLastInSet={
                        index === tasks.length - 1 &&
                        newTasks.length === 0 &&
                        cancelledTasksToShow.length === 0
                      }
                      showExtraColumnsBecauseOfEnforcedComments={
                        showExtraColumnsBecauseOfEnforcedComments
                      }
                      isTaskTypeIdSwapable={swapabilityInfo != null}
                      swapabilityInfo={swapabilityInfo}
                      isReviewQuestion={isReviewQuestion}
                      isSettingQuestion={isSettingQuestion}
                      isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                    />
                  ))}
                {doesModeAllowForTaskCreation &&
                  newTasks.map((task, index) => (
                    <AdvancedTaskDesktopItem
                      key={task.taskId}
                      userContext={userContext}
                      task={task}
                      taskType={taskType}
                      config={config}
                      isReadOnly={effectiveReadOnly}
                      formType={formType}
                      displayRatingColumn={displayRatingColumn}
                      columnCount={columnCount}
                      loggedInUserId={userContext.user.id}
                      isLoggedInUserTheSubjectUser={
                        isLoggedInUserTheSubjectUser
                      }
                      subjectUser={subjectUserFullDetails}
                      nonSubjectUser={nonSubjectUser}
                      participants={participants}
                      onDropdownMenuItemClick={handleTaskDropDownItemClick}
                      onViewButtonClick={handleTaskViewButtonClick}
                      taskTypeIcon={taskTypeIcon}
                      isLastInSet={
                        index === newTasks.length - 1 &&
                        cancelledTasksToShow.length === 0
                      }
                      showExtraColumnsBecauseOfEnforcedComments={
                        showExtraColumnsBecauseOfEnforcedComments
                      }
                      isTaskTypeIdSwapable={swapabilityInfo != null}
                      swapabilityInfo={swapabilityInfo}
                      isReviewQuestion={isReviewQuestion}
                      isSettingQuestion={isSettingQuestion}
                      isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                    />
                  ))}
                {cancelledTasksToShow.map((cancelledTask, index) => (
                    <AdvancedTaskDesktopItem
                      key={cancelledTask.taskId}
                      userContext={userContext}
                      task={cancelledTask}
                      taskType={taskType}
                      config={config}
                      isCancelled={true}
                      isReadOnly={effectiveReadOnly}
                      formType={formType}
                      displayRatingColumn={displayRatingColumn}
                      columnCount={columnCount}
                      loggedInUserId={userContext.user.id}
                      isLoggedInUserTheSubjectUser={
                        isLoggedInUserTheSubjectUser
                      }
                      subjectUser={subjectUserFullDetails}
                      nonSubjectUser={nonSubjectUser}
                      participants={participants}
                      onDropdownMenuItemClick={handleTaskDropDownItemClick}
                      onViewButtonClick={handleTaskViewButtonClick}
                      taskTypeIcon={taskTypeIcon}
                      isLastInSet={index === cancelledTasksToShow.length - 1}
                      showExtraColumnsBecauseOfEnforcedComments={
                        showExtraColumnsBecauseOfEnforcedComments
                      }
                      isTaskTypeIdSwapable={swapabilityInfo != null}
                      swapabilityInfo={swapabilityInfo}
                      isReviewQuestion={isReviewQuestion}
                      isSettingQuestion={isSettingQuestion}
                      isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                    />
                  ))}
              </div>
            </div>
          </div>

          {/* Mobile Only */}
          <div className="block sm:hidden mb-2">
            <div className="border outline outline-1 outline-gray-400 rounded-sm">
              {config.questionMode != "TASK-CREATION-ONLY" &&
                tasks.map((task) => (
                  <AdvancedTaskMobileItem
                    key={task.taskId}
                    userContext={userContext}
                    task={task}
                    taskType={taskType}
                    config={config}
                    isReadOnly={effectiveReadOnly}
                    formType={formType}
                    displayRatingColumn={displayRatingColumn}
                    columnCount={columnCount}
                    loggedInUserId={userContext.user.id}
                    isLoggedInUserTheSubjectUser={isLoggedInUserTheSubjectUser}
                    subjectUsersName={subjectUserName}
                    nonSubjectUsersName={nonSubjectUserName}
                    onDropdownMenuItemClick={handleTaskDropDownItemClick}
                    onViewButtonClick={handleTaskViewButtonClick}
                    isTaskTypeIdSwapable={swapabilityInfo != null}
                    swapabilityInfo={swapabilityInfo}
                    isReviewQuestion={isReviewQuestion}
                    isSettingQuestion={isSettingQuestion}
                    isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                  />
                ))}
              {doesModeAllowForTaskCreation &&
                newTasks.map((task) => (
                  <AdvancedTaskMobileItem
                    key={task.taskId}
                    userContext={userContext}
                    task={task}
                    taskType={taskType}
                    config={config}
                    isReadOnly={effectiveReadOnly}
                    formType={formType}
                    displayRatingColumn={displayRatingColumn}
                    columnCount={columnCount}
                    loggedInUserId={userContext.user.id}
                    isLoggedInUserTheSubjectUser={isLoggedInUserTheSubjectUser}
                    subjectUsersName={subjectUserName}
                    nonSubjectUsersName={nonSubjectUserName}
                    onDropdownMenuItemClick={handleTaskDropDownItemClick}
                    onViewButtonClick={handleTaskViewButtonClick}
                    isTaskTypeIdSwapable={swapabilityInfo != null}
                    swapabilityInfo={swapabilityInfo}
                    isReviewQuestion={isReviewQuestion}
                    isSettingQuestion={isSettingQuestion}
                    isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                  />
                ))}
              {cancelledTasksToShow.map((cancelledTask) => (
                  <AdvancedTaskMobileItem
                    key={cancelledTask.taskId}
                    userContext={userContext}
                    task={cancelledTask}
                    taskType={taskType}
                    config={config}
                    isCancelled={true}
                    isReadOnly={effectiveReadOnly}
                    formType={formType}
                    displayRatingColumn={displayRatingColumn}
                    columnCount={columnCount}
                    loggedInUserId={userContext.user.id}
                    isLoggedInUserTheSubjectUser={isLoggedInUserTheSubjectUser}
                    subjectUsersName={subjectUserName}
                    nonSubjectUsersName={nonSubjectUserName}
                    onDropdownMenuItemClick={handleTaskDropDownItemClick}
                    onViewButtonClick={handleTaskViewButtonClick}
                    isTaskTypeIdSwapable={swapabilityInfo != null}
                    swapabilityInfo={swapabilityInfo}
                    isReviewQuestion={isReviewQuestion}
                    isSettingQuestion={isSettingQuestion}
                    isDualPrepPlanningForm={isDualPrepPlanningFormContext}
                  />
                ))}
            </div>
          </div>

          {showOverdueTasksBanner && (
            <WarningAlert
              prefix={null}
              message={t("Common.Validation.Errors.OverdueTasks")}
            />
          )}

          {showCreateTaskButton && (
            <div
              className={cx(
                formType === "COLLAB-DOC" ? "pb-2 pt-1" : "pb-1 mb-2"
              )}
            >
              {addButtonComponent}
            </div>
          )}

          {taskType &&
            shouldShowTaskWeightingBanner(config.questionMode, taskType) && (
              <TaskWeightingBanner
                tasks={tasks}
                taskType={taskType}
                formType={formType}
              />
            )}

          {/* Show validity of tasks based on enforced comments  */}
          {!effectiveReadOnly &&
            !isDualPrepPlanning &&
            config.enforceComments &&
            config.liveAndNewTasks.some((x) => x.enforcedComments != null) &&
            taskType && (
              <EnforcedCommentsValidityBanner
                config={config}
                taskType={taskType}
                formType={formType}
              />
            )}
        </>
      )}
      {!userHasTasksToShow && taskType && (
        <div className="text-center text-gray-400 grow w-full">
          <div className="mt-3 lg:mt-5 ">
            <FontAwesomeIcon
              color={"#D1D5DB"}
              size="3x"
              icon={taskTypeIconHelper.getTaskTypeIcon(taskType.iconType)}
            />
          </div>
          <div
            className={cx(
              "mt-2 mb-2 !text-sm",
              formType === "COLLAB-DOC" ? "text-gray-700" : "text-white"
            )}
          >
            <p>{emptyStateMessage}</p>
            {showCreateTaskButton && (
              <div className="text-center pb-1 mt-1">{addButtonComponent}</div>
            )}
          </div>
          {taskType &&
            shouldShowTaskWeightingBanner(config.questionMode, taskType) && (
              <TaskWeightingBanner
                tasks={tasks}
                taskType={taskType}
                formType={formType}
              />
            )}
        </div>
      )}
      {taskType && (
        <>
          <AddAdvancedTaskPopup
            usageScenario="JOURNEY-COLLAB-DOC"
            taskType={taskType}
            activeTasks={activeTasks ?? []}
            maxOverallTaskRestriction={taskValidationCounts.max}
            categories={taskType.categories ?? []}
            ownerEmployee={subjectUser}
            isOpen={isAddTaskModalOpen}
            createdInAnswerSetUniqueId={currentAnswerSetUniqueId}
            formId={formId}
            onOpenChange={setIsAddTaskModalOpen}
            onSuccessfulCreation={onAddNewTaskSuccessful}
          />
          <ExistingAdvancedTaskPopup
            usageScenario="JOURNEY-COLLAB-DOC"
            taskType={taskType}
            userTaskId={selectedUserTaskId}
            categories={taskType.categories ?? []}
            isOpen={isExistingTaskModalOpen}
            isTaskNewlyCreatedInJourney={selectedUserTaskIsNewlyCreated}
            initialTab={selectedUserTaskTabToOpenTo}
            initialFormToDisplay={selectedFormToDisplay}
            snapshotDate={selectedTaskSnapshotDate}
            isLocked={isExistingAdvancedTaskPopupLocked({
              isQuestionReadOnly: effectiveReadOnly,
              snapshotDate: selectedTaskSnapshotDate,
            })}
            currentAnswerSetUniqueId={currentAnswerSetUniqueId}
            showAddCommentInFormLikeStyle={showAddCommentInFormLikeStyle}
            subjectUserName={subjectUserName}
            formId={formId}
            onOpenChange={handleExistingModalOpenChange}
            onSuccessfulUpdate={() => handleExistingModalOpenChange(false)}
            onSuccessfulCancel={() => handleExistingModalOpenChange(false)}
            onSuccessfulClose={() => handleExistingModalOpenChange(false)}
            onSuccessfulReOpen={() => handleExistingModalOpenChange(false)}
            onSuccessfulSavedForLater={() =>
              handleExistingModalOpenChange(false)
            }
            onSuccessfulRemove={handleExistingModalRemoveTaskSuccess}
            onCommentCountChanged={() => {}}
          />
        </>
      )}
    </>
  );
}

export default TaskManagementQuestion;
